/**
 * ActivityFeed.jsx - Attention Queue & Activity Feed Component
 * 
 * Checklist Items Covered:
 * - F1: Attention Queue Header
 * - F2: Pending Actions Count
 * - F3: Action Item Cards
 * - F4: Activity Feed Header
 * - F5: Activity Item Row
 * - F6: Activity Channel Icon
 * - F7: Activity Status Badge
 * - F8: Activity Timestamp
 */

import React, { useMemo } from 'react';
import { 
  Flag, Mail, MailOpen, MessageSquare, CheckCircle, XCircle, 
  Clock, Sparkles, AlertTriangle, ExternalLink
} from 'lucide-react';
import { COLORS } from './messagingStyles';

// ─── Styles ─────────────────────────────────────────────────────────────────────

const styles = {
  // Container
  feedContainer: {
    position: 'relative',
    borderRadius: 18,
    border: `1px solid ${COLORS.borderMedium}`,
    overflow: 'hidden',
    background: 'linear-gradient(180deg, rgba(255,255,255,0.88) 0%, rgba(255,252,246,0.64) 50%, rgba(255,248,234,0.32) 100%)',
  },
  feedNeonTopBar: {
    position: 'absolute',
    top: 0,
    left: 20,
    right: 20,
    height: 2,
    background: `linear-gradient(90deg, transparent, ${COLORS.gold}, transparent)`,
    borderRadius: 1,
  },
  feedContent: {
    position: 'relative',
    zIndex: 1,
    padding: 20,
  },

  // Header Section
  feedHeaderRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  feedEyebrow: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '1px',
    textTransform: 'uppercase',
    color: COLORS.goldDark,
    marginBottom: 4,
  },
  feedTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: COLORS.textPrimary,
    letterSpacing: '-0.2px',
  },
  statusChip: {
    padding: '5px 10px',
    borderRadius: 6,
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  statusChipHealthy: {
    background: COLORS.successLight,
  },
  statusChipWatch: {
    background: COLORS.warningLight,
  },
  statusChipAttention: {
    background: COLORS.errorLight,
  },
  statusTextHealthy: {
    color: COLORS.success,
  },
  statusTextWatch: {
    color: '#92400e',
  },
  statusTextAttention: {
    color: COLORS.error,
  },

  // Spotlight Card (Current State)
  spotlightCard: {
    padding: 16,
    borderRadius: 14,
    background: 'rgba(255,255,255,0.7)',
    border: `1px solid ${COLORS.borderLight}`,
    marginBottom: 16,
  },
  spotlightLabel: {
    fontSize: 10,
    fontWeight: 600,
    color: COLORS.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: 6,
  },
  spotlightTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: COLORS.textPrimary,
    marginBottom: 4,
  },
  spotlightBody: {
    fontSize: 12,
    color: COLORS.textMuted,
    lineHeight: 1.5,
  },

  // F1-F3: Attention Queue
  attentionQueueCard: {
    padding: 16,
    borderRadius: 14,
    background: 'rgba(220,38,38,0.06)',
    border: '1px solid rgba(220,38,38,0.15)',
    marginBottom: 16,
  },
  attentionQueueHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  attentionQueueTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: '#B24E4E',
  },
  attentionBadge: {
    marginLeft: 'auto',
    padding: '3px 8px',
    borderRadius: 6,
    background: 'rgba(220,38,38,0.15)',
    fontSize: 10,
    fontWeight: 700,
    color: '#B24E4E',
  },
  attentionItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 0',
    borderBottom: '1px solid rgba(220,38,38,0.1)',
  },
  attentionItemLast: {
    borderBottom: 'none',
  },
  attentionItemDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    background: '#B24E4E',
    flexShrink: 0,
  },
  attentionItemGuest: {
    fontSize: 13,
    fontWeight: 600,
    color: COLORS.textPrimary,
  },
  attentionItemDetail: {
    fontSize: 11,
    color: COLORS.textMuted,
    marginTop: 2,
  },

  // F4-F8: Activity Feed
  activityFeedCard: {
    padding: 16,
    borderRadius: 14,
    background: 'rgba(255,255,255,0.6)',
    border: `1px solid ${COLORS.borderLight}`,
  },
  activityFeedHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 14,
  },
  activityFeedTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: COLORS.textPrimary,
  },
  activityFeedCount: {
    fontSize: 11,
    color: COLORS.textMuted,
  },
  
  // Activity Row
  activityRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '10px 0',
    borderBottom: `1px solid ${COLORS.borderLight}`,
  },
  activityRowLast: {
    borderBottom: 'none',
  },
  activityDotWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexShrink: 0,
  },
  activityDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  activityDotSuccess: {
    background: COLORS.gold,
  },
  activityDotDanger: {
    background: '#B24E4E',
  },
  activityDotWatch: {
    background: '#8A7241',
  },
  activityLabelBadge: {
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: '0.5px',
    color: COLORS.textMuted,
    marginBottom: 2,
  },
  activityGuestName: {
    fontSize: 12,
    fontWeight: 600,
    color: COLORS.textPrimary,
  },
  activityMeta: {
    marginLeft: 'auto',
    textAlign: 'right',
  },
  activityTime: {
    fontSize: 10,
    color: COLORS.textMuted,
  },

  // Empty State
  activityEmpty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
    textAlign: 'center',
  },
  activityEmptyTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: COLORS.textPrimary,
    marginTop: 12,
    marginBottom: 6,
  },
  activityEmptyBody: {
    fontSize: 12,
    color: COLORS.textMuted,
    maxWidth: 200,
    lineHeight: 1.5,
  },
};

// ─── Helper Functions ───────────────────────────────────────────────────────────

const formatTimeAgo = (dateStr) => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
};

const getActivityLabel = (status) => {
  switch (status?.toLowerCase()) {
    case 'clicked': return 'INVITE_CLICKED';
    case 'opened': return 'INVITE_OPENED';
    case 'failed': return 'FAILED';
    case 'sent': return 'EMAIL_SENT';
    default: return 'EMAIL_SENT';
  }
};

const getActivityIcon = (status) => {
  switch (status?.toLowerCase()) {
    case 'clicked': return CheckCircle;
    case 'opened': return MailOpen;
    case 'failed': return XCircle;
    default: return Mail;
  }
};

const getActivityIconColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'clicked':
    case 'opened':
      return COLORS.gold;
    case 'failed':
      return '#B24E4E';
    default:
      return COLORS.goldDark;
  }
};

const getDotStyle = (status) => {
  switch (status?.toLowerCase()) {
    case 'clicked':
    case 'opened':
      return styles.activityDotSuccess;
    case 'failed':
      return styles.activityDotDanger;
    default:
      return styles.activityDotWatch;
  }
};

// ─── Component ──────────────────────────────────────────────────────────────────

const ActivityFeed = ({
  emailLogs = [],
  maxItems = 8,
}) => {
  // Process logs into activity rows
  const activityRows = useMemo(() => {
    return emailLogs
      .slice(0, maxItems)
      .map(log => ({
        id: log.id || log._id,
        guest: log.recipient_name || log.email || 'Unknown',
        email: log.email,
        channel: 'Email',
        template: log.template_name || 'Template',
        status: log.status || 'Sent',
        deliveryResult: log.delivery_result || log.error_message || '',
        timestamp: log.sent_at || log.created_at,
        dateLabel: formatTimeAgo(log.sent_at || log.created_at),
      }));
  }, [emailLogs, maxItems]);

  // Failed items for attention queue
  const failedItems = useMemo(() => {
    return activityRows.filter(r => r.status?.toLowerCase() === 'failed');
  }, [activityRows]);

  // Compute operational status
  const operationsStatus = useMemo(() => {
    if (failedItems.length > 0) {
      return {
        tone: 'attention',
        title: 'Action Required',
        body: `${failedItems.length} send${failedItems.length !== 1 ? 's' : ''} failed and may need attention. Review the queue below.`,
      };
    }
    if (activityRows.length === 0) {
      return {
        tone: 'healthy',
        title: 'Ready to Send',
        body: 'No recent activity. Arm a message and select recipients to begin.',
      };
    }
    const recentOpens = activityRows.filter(r => 
      r.status?.toLowerCase() === 'opened' || r.status?.toLowerCase() === 'clicked'
    );
    if (recentOpens.length > 0) {
      return {
        tone: 'healthy',
        title: 'Engagement Active',
        body: `${recentOpens.length} recipient${recentOpens.length !== 1 ? 's' : ''} opened or clicked recently. Send path is healthy.`,
      };
    }
    return {
      tone: 'watch',
      title: 'Monitoring',
      body: 'Messages sent. Watching for opens and clicks.',
    };
  }, [activityRows, failedItems]);

  return (
    <div style={styles.feedContainer} data-testid="activity-feed">
      <div style={styles.feedNeonTopBar} />
      
      <div style={styles.feedContent}>
        {/* Header */}
        <div style={styles.feedHeaderRow}>
          <div>
            <div style={styles.feedEyebrow}>Attention & Activity</div>
            <div style={styles.feedTitle}>Operational Feed</div>
          </div>
          
          {/* Status Chip */}
          <div
            style={{
              ...styles.statusChip,
              ...(operationsStatus.tone === 'healthy' ? styles.statusChipHealthy :
                  operationsStatus.tone === 'watch' ? styles.statusChipWatch :
                  styles.statusChipAttention),
            }}
            data-testid="operations-status"
          >
            <span
              style={
                operationsStatus.tone === 'healthy' ? styles.statusTextHealthy :
                operationsStatus.tone === 'watch' ? styles.statusTextWatch :
                styles.statusTextAttention
              }
            >
              {operationsStatus.tone === 'healthy' ? 'Ready' :
               operationsStatus.tone === 'watch' ? 'Watch' : 'Action'}
            </span>
          </div>
        </div>

        {/* Spotlight Card - Current State */}
        <div style={styles.spotlightCard} data-testid="spotlight-card">
          <div style={styles.spotlightLabel}>Current Send State</div>
          <div style={styles.spotlightTitle}>{operationsStatus.title}</div>
          <div style={styles.spotlightBody}>{operationsStatus.body}</div>
        </div>

        {/* F1-F3: Attention Queue (only if failed items) */}
        {failedItems.length > 0 && (
          <div style={styles.attentionQueueCard} data-testid="attention-queue">
            <div style={styles.attentionQueueHeader}>
              <Flag size={13} color="#B24E4E" />
              <span style={styles.attentionQueueTitle}>Attention Queue</span>
              {/* F2: Pending Actions Count */}
              <span style={styles.attentionBadge} data-testid="attention-count">
                {failedItems.length}
              </span>
            </div>
            
            {/* F3: Action Item Cards */}
            {failedItems.slice(0, 3).map((item, i) => (
              <div
                key={item.id}
                style={{
                  ...styles.attentionItem,
                  ...(i === Math.min(failedItems.length, 3) - 1 ? styles.attentionItemLast : {}),
                }}
                data-testid={`attention-item-${item.id}`}
              >
                <div style={styles.attentionItemDot} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={styles.attentionItemGuest}>{item.guest}</div>
                  <div style={styles.attentionItemDetail}>
                    {item.deliveryResult || 'Delivery failed'}
                  </div>
                </div>
                <Flag size={12} color="#B24E4E" style={{ flexShrink: 0 }} />
              </div>
            ))}
          </div>
        )}

        {/* F4-F8: Activity Feed */}
        <div style={styles.activityFeedCard} data-testid="activity-feed-card">
          {/* F4: Activity Feed Header */}
          <div style={styles.activityFeedHeader}>
            <span style={styles.activityFeedTitle}>Recent Guest Activity</span>
            <span style={styles.activityFeedCount}>{activityRows.length} shown</span>
          </div>

          {activityRows.length === 0 ? (
            <div style={styles.activityEmpty}>
              <Sparkles size={18} color={COLORS.gold} />
              <div style={styles.activityEmptyTitle}>No recent activity yet</div>
              <div style={styles.activityEmptyBody}>
                Your first verified send will populate this timeline.
              </div>
            </div>
          ) : (
            activityRows.map((row, i) => {
              const IconComponent = getActivityIcon(row.status);
              const iconColor = getActivityIconColor(row.status);
              const dotStyle = getDotStyle(row.status);
              
              return (
                <div
                  key={row.id}
                  style={{
                    ...styles.activityRow,
                    ...(i === activityRows.length - 1 ? styles.activityRowLast : {}),
                  }}
                  data-testid={`activity-row-${row.id}`}
                >
                  {/* F6: Channel Icon */}
                  <div style={styles.activityDotWrap}>
                    <div style={{ ...styles.activityDot, ...dotStyle }} />
                    <IconComponent size={13} color={iconColor} />
                  </div>
                  
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {/* F7: Status Badge */}
                    <div style={styles.activityLabelBadge}>
                      {getActivityLabel(row.status)}
                    </div>
                    {/* F5: Guest Name */}
                    <div style={styles.activityGuestName}>{row.guest}</div>
                  </div>
                  
                  {/* F8: Timestamp */}
                  <div style={styles.activityMeta}>
                    <div style={styles.activityTime}>{row.dateLabel}</div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default ActivityFeed;
